import { AnsweringClient, PaginationParams } from "../../../../clients/answeringClient";
import { describeApiError, isExpiredSession } from "../../../../utils/apiError";

export interface IAssignment {
  applicationId: number;
  title: string;
  dueDate: Date;
  instrumentTitle: string;
}

/** Campos que o schema do convite valida, na grafia usada pelo backend. */
export type InviteField =
  | "firstName"
  | "lastName"
  | "email"
  | "assignedApplications"
  | "recaptchaToken";

export interface InviteError {
  message: string;
  /** Preenchido apenas em 422, para destacar o campo recusado no formulário. */
  field?: InviteField;
}

export interface InviteUserModalControllerDelegate {
  didGetAssignments: (assignments: IAssignment[]) => void;
  didFailGetAssignments: (message: string) => void;
  /**
   * `emailDelivered` is `false` only when an e-mail was owed and nothing was
   * delivered — the invitation itself succeeded either way, so this is a
   * warning to show, never a failure to report.
   */
  didInviteUser: (emailDelivered: boolean | null) => void;
  didFailInviteUser: (error: InviteError) => void;
  applicationShouldLogout: () => void;
}

const INVITE_FIELDS: string[] = [
  "firstName",
  "lastName",
  "email",
  "assignedApplications",
  "recaptchaToken",
];

/**
 * Um 422 é sempre erro de schema (Joi), tanto no instrument-service quanto no
 * auth-service — que o propaga preservando `details`. O campo recusado vem em
 * `details.path`; quando não vem, sobra o nome entre aspas abrindo a mensagem
 * (`"email" must be a valid email`).
 */
function extractInvalidField(error: any): InviteField | undefined {
  if (error?.response?.status !== 422) {
    return undefined;
  }
  const data = error.response.data;
  const path = Array.isArray(data?.details?.path) ? data.details.path[0] : undefined;
  const quoted = /^"([^"]+)"/.exec(data?.message ?? "")?.[1];
  const field = path ?? quoted;
  return INVITE_FIELDS.includes(field) ? (field as InviteField) : undefined;
}

export function describeInviteError(error: any, fallback: string): InviteError {
  return {
    message: describeApiError(error, fallback),
    field: extractInvalidField(error),
  };
}

export class InviteUserModalController {
  constructor(
    private delegate: InviteUserModalControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  public async getManagedAssignments(
    accessToken: string,
    options?: PaginationParams,
    hasRetried = false
  ): Promise<void> {
    try {
      const response = await this.answeringClient.getManagedAssignments(
        accessToken,
        options
      );
      const data = response.data.data as IAssignment[];
      const now = new Date();
      const filteredData = data.filter((assignment) => {
        return new Date(assignment.dueDate) > now;
      });
      this.delegate.didGetAssignments(filteredData);
    } catch (error: any) {
      if (isExpiredSession(error) && !hasRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        return this.getManagedAssignments(newTokens.accessToken, options, true);
      }
      this.delegate.didFailGetAssignments(
        describeApiError(
          error,
          "Não foi possível carregar as aplicações disponíveis."
        )
      );
    }
  }

  public async inviteUser(
    accessToken: string,
    firstName: string,
    lastName: string,
    email: string,
    assignmentIds: number[],
    recaptchaToken: string,
    hasRetried = false
  ): Promise<void> {
    try {
      const response = await this.answeringClient.inviteUser(
        accessToken,
        firstName,
        lastName,
        email,
        assignmentIds,
        recaptchaToken
      );
      // An older backend answered 204 with no body; absent reads as `null`,
      // which the screen treats as ordinary success.
      this.delegate.didInviteUser(response.data?.emailDelivered ?? null);
    } catch (error: any) {
      if (isExpiredSession(error) && !hasRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        return this.inviteUser(
          newTokens.accessToken,
          firstName,
          lastName,
          email,
          assignmentIds,
          recaptchaToken,
          true
        );
      }
      this.delegate.didFailInviteUser(
        describeInviteError(
          error,
          "Não foi possível enviar o convite. Tente novamente em instantes."
        )
      );
    }
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }
}
